import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Home from './views/Home';
import Create from './views/products/Create';
import Remix from './views/products/Remix';
import Template from './views/products/Template';
import TemplateGroup from './views/products/TemplateGroup';
import Products from './views/products/Products';
import ProtectedRoute from './auth/ProtectedRoute';
import LoginCallback from './auth/Auth0Callback';
import { Navigation } from './components/Navigation';
import { ShoppingCartProvider } from './context/ShoppingCartContext';
import Success from './views/payment/Success';
import Cancel from './views/payment/Cancel';
import OrderDetail from './views/order/OrderDetail';
import OrderError from './views/order/OrderError';
import Account from './views/account/Account';
import Designs from './views/account/Designs';
import NotFound from './views/NotFound';
import './styles/global.css';
import Footer from './components/Footer';
import Privacy from './views/policies/Privacy';
import Terms from './views/policies/Terms';
import Shipping from './views/policies/Shipping';
import Returns from './views/policies/Returns';
import License from './views/policies/License';
import ContactUs from './views/contact/ContactUs';
import Sitemap from './views/Sitemap';
import Product from './views/products/Product';
import ScrollToTop from './components/ScrollToTop';
import { ToastContainer } from 'react-toastify';
import AnalyticsService from './services/AnalyticsService';

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ShoppingCartProvider>
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navigation />
        <main style={{ flex: 1, paddingTop: "16px" }}>{children}</main>
        <Footer />
      </div>
    </ShoppingCartProvider >
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Route without Header, Footer, or ShoppingCartProvider */}
        <Route path="/sitemap.xml" element={<Sitemap />} />

        {/* All other routes wrapped in AppLayout */}
        <Route path="/*" element={<AppWithLayout />} />
      </Routes>
    </Router>
  );
};

// Analytics Tracker Component
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    AnalyticsService.trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

// App Layout Wrapper (Excludes Sitemap)
const AppWithLayout: React.FC = () => {
  return (
    <AppLayout>
      <AnalyticsTracker />
      <ScrollToTop />
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/callback" element={<LoginCallback />} />

        {/* Protected layout (no path) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/products/create" element={<Create />} />
          <Route path="/products/remix" element={<Remix />} />
          <Route path="/account" element={<Account />} />
          <Route path="/designs" element={<Designs />} />
        </Route>

        {/* Templates */}
        <Route path="/templates/:groupId" element={<TemplateGroup />} />

        {/* Products */}
        <Route path="/products">
          <Route index element={<Products />} /> {/* was path="" */}
          <Route path="template/:productId" element={<Template />} />
          <Route path=":catalogItemId/:category/:productId" element={<Product />} />
          <Route path=":productId" element={<Product />} />
        </Route>

        {/* Payment */}
        <Route path="/payment">
          <Route path="success" element={<Success />} />
          <Route path="cancel" element={<Cancel />} />
        </Route>

        {/* Order */}
        <Route path="/order">
          <Route path="error" element={<OrderError />} />
          <Route path=":orderId" element={<OrderDetail />} />
        </Route>

        {/* Static */}
        <Route path="/contactus" element={<ContactUs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/license" element={<License />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ToastContainer />
    </AppLayout>
  );
};

export default App;